import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { EASE, SectionTag, MaskedLine } from "./Motion";

// Swap these sample campaign files with real client results when ready.
const cases = [
  {
    id: "case-card-1",
    client: "Glowripples Skincare",
    category: "D2C LAUNCH",
    summary:
      "A bootstrapped skincare label went from unknown to unavoidable — creator seeding plus precision SMM across Instagram and YouTube Shorts.",
    duration: "90 DAYS",
    channel: "INSTAGRAM + SHORTS",
    metrics: [
      { label: "FOLLOWERS", before: "4.2K", after: "310K" },
      { label: "ENGAGEMENT", before: "0.8%", after: "6.4%" },
      { label: "LAUNCH REVENUE", before: "₹0", after: "₹1.2 Cr" },
    ],
  },
  {
    id: "case-card-2",
    client: "Arjun Vlogs",
    category: "CREATOR DEALS",
    summary:
      "A tech vlogger with loyal viewers but no rate card. We packaged his audience, brokered five deals and put contracts behind every handshake.",
    duration: "6 MONTHS",
    channel: "YOUTUBE + INSTAGRAM",
    metrics: [
      { label: "BRAND REVENUE", before: "₹0", after: "₹18L" },
      { label: "DEALS CLOSED", before: "0", after: "5" },
      { label: "CAMPAIGN REACH", before: "—", after: "2.4M" },
    ],
  },
  {
    id: "case-card-3",
    client: "NovaAudio",
    category: "SMART SOURCING",
    summary:
      "An audio accessories brand was bleeding margin to middlemen. Our desk renegotiated at the factory gate and rebuilt the supply line.",
    duration: "45 DAYS",
    channel: "SHENZHEN → MUMBAI",
    metrics: [
      { label: "LANDED COST", before: "₹1,450", after: "₹890" },
      { label: "LEAD TIME", before: "38 DAYS", after: "19 DAYS" },
      { label: "FIRST BATCH", before: "—", after: "4,200 SOLD" },
    ],
  },
];

const Metric = ({ m, testid }) => (
  <div className="border-l border-white/10 pl-5" data-testid={testid}>
    <div className="font-mono text-[9px] tracking-[0.25em] text-neutral-500">{m.label}</div>
    <div className="mt-3 text-sm font-medium text-neutral-500">{m.before}</div>
    <motion.div
      initial={{ scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, amount: 0.8 }}
      transition={{ duration: 1.2, ease: EASE, delay: 0.4 }}
      className="mt-2 h-px w-full origin-left bg-gradient-to-r from-gold/70 to-transparent"
    />
    <div className="mt-2 flex items-center gap-2">
      <ArrowRight size={12} className="shrink-0 text-gold" />
      <span className="whitespace-nowrap font-serif text-2xl italic text-gold-grad sm:text-3xl">
        {m.after}
      </span>
    </div>
  </div>
);

export default function CaseStudies() {
  return (
    <section id="results" data-testid="results-section" className="relative py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-16">
        <SectionTag index="10" label="Case Files" testid="results-tag" />
        <h2 className="mt-6 max-w-3xl text-4xl font-bold tracking-tight text-neutral-50 sm:text-5xl">
          <MaskedLine>Numbers that</MaskedLine>
          <MaskedLine delay={0.12} innerClassName="font-serif italic font-medium text-gold-grad">
            survived the audit.
          </MaskedLine>
        </h2>
        <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-neutral-400">
          A peek inside the campaign files — the before, the after, and everything we engineered
          in between.
        </p>

        <div className="mt-16 space-y-6 lg:space-y-8">
          {cases.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 1, ease: EASE, delay: i * 0.08 }}
              className="group relative overflow-hidden rounded-2xl border border-gold/15 bg-panel/80 p-8 backdrop-blur-xl transition-colors duration-500 hover:border-gold/45 sm:p-10"
              data-testid={c.id}
            >
              <span className="pointer-events-none absolute -right-4 -top-12 select-none font-serif text-[10rem] italic leading-none text-white/[0.04] transition-colors duration-700 group-hover:text-gold/[0.08] sm:text-[12rem]">
                0{i + 1}
              </span>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-gold/[0.06] to-transparent opacity-0 transition-opacity duration-700 group-hover:opacity-100" />

              <div className="relative grid gap-10 lg:grid-cols-12">
                <div className="lg:col-span-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-gold/70">
                      CASE 0{i + 1}
                    </span>
                    <span className="rounded-full border border-white/10 px-3 py-1 font-mono text-[9px] tracking-[0.2em] text-neutral-400">
                      {c.category}
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif text-3xl italic text-neutral-50 sm:text-4xl">
                    {c.client}
                  </h3>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-400 sm:text-base">
                    {c.summary}
                  </p>
                  <div className="mt-6 flex items-center gap-5 font-mono text-[10px] tracking-[0.2em] text-neutral-500">
                    <span>{c.duration}</span>
                    <span className="text-gold/50">/</span>
                    <span>{c.channel}</span>
                  </div>
                </div>

                <div className="grid gap-8 sm:grid-cols-3 lg:col-span-7 lg:items-center">
                  {c.metrics.map((m, j) => (
                    <Metric key={m.label} m={m} testid={`${c.id}-metric-${j + 1}`} />
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
